<template>
	<JFlex class="s-w-screen s-h-screen">
		<!-- Left: Component Tree -->
		<JFlex class="s-flex-col s-w-3/12 s-h-full s-border-r-2 s-border-gray-300" v-if="!isExpanded">
			<JText class="s-text-center s-font-bold s-pt-4 s-pb-2 s-border-b-2 s-border-indigo-500 s-bg-white s-select-none">
				{{ $t("uiEditor.tree") }}
			</JText>
			<BaseComponentEditorTree
				ref="BaseComponentEditorTree"
				class="s-h-full s-px-4 s-overflow-scroll"
				:sourceTree="tree"
				:getTreeSkeleton="getTreeSkeleton"
				:nodeValue="nodeValue"
				@selectNode="handleSelectNode"
			/>
		</JFlex>

		<!-- Center: Canvas -->
		<JBox class="s-relative s-h-full" :class="[isExpanded ? 's-w-full' : 's-w-6/12']">
			<JBox class="s-w-full s-h-full s-bg-white s-p-24" v-if="nodeValue !== null">
				<!-- TODO: Add Canvas Here -->
				<JText v-for="(value, index) in nodeValue.split(' ')" :key="index"> {{ value }}</JText>
			</JBox>
			<!-- Idle Canvas -->
			<JFlex class="s-flex-col s-w-full s-h-full s-items-center" v-else>
				<JFlex class="s-flex-col s-my-28">
					<JText class="s-text-3xl s-text-gray-500 s-font-bold">
						{{ $t("uiEditor.componentThemeEditor") }}
					</JText>
					<JText class="s-self-center s-text-sm s-text-gray-400 s-translate-x-20">
						{{ $t("uiEditor.by") }} <span class="s-font-bold"> {{ $t("uiEditor.coverStudio") }} </span>
					</JText>
				</JFlex>
				<JText
					class="s-h-20 s-text-xl s-text-gray-400 s-font-bold s-border-4 s-border-gray-400 s-border-dashed s-rounded-lg s-px-6 s-py-5 s-select-none"
				>
					{{ $t("uiEditor.selectToEdit") }}
				</JText>
			</JFlex>

			<JBox class="s-absolute s-right-0 s-p-2" style="bottom: 50px;">
				<!-- Expand -->
				<JFlex
					class="s-h-12 s-w-12 s-rounded-full s-bg-purple-800 s-shadow-lg s-text-white s-items-center s-justify-center s-cursor-pointer s-m-2"
					@click="isExpanded = !isExpanded"
				>
					<svg class="s-text-white s-h-6 s-w-6" fill="currentColor" viewBox="0 0 20 20">
						<path
							fill-rule="evenodd"
							d="M3 4a1 1 0 011-1h4a1 1 0 010 2H6.414l2.293 2.293a1 1 0 11-1.414 1.414L5 6.414V8a1 1 0 01-2 0V4zm9 1a1 1 0 010-2h4a1 1 0 011 1v4a1 1 0 01-2 0V6.414l-2.293 2.293a1 1 0 11-1.414-1.414L13.586 5H12zm-9 7a1 1 0 012 0v1.586l2.293-2.293a1 1 0 111.414 1.414L6.414 15H8a1 1 0 010 2H4a1 1 0 01-1-1v-4zm13-1a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 010-2h1.586l-2.293-2.293a1 1 0 111.414-1.414L15 13.586V12a1 1 0 011-1z"
							clip-rule="evenodd"
						></path>
					</svg>
				</JFlex>
				<!-- Save -->
				<JFlex
					class="s-h-12 s-w-12 s-rounded-full s-bg-purple-800 s-shadow-lg s-text-white s-items-center s-justify-center s-cursor-pointer s-m-2"
					@click="handleSave()"
				>
					<svg class="s-text-white s-h-6 s-w-6" fill="currentColor" viewBox="0 0 20 20">
						<path
							fill-rule="evenodd"
							d="M8 4a3 3 0 00-3 3v4a5 5 0 0010 0V7a1 1 0 112 0v4a7 7 0 11-14 0V7a5 5 0 0110 0v4a3 3 0 11-6 0V7a1 1 0 012 0v4a1 1 0 102 0V7a3 3 0 00-3-3z"
							clip-rule="evenodd"
						></path>
					</svg>
				</JFlex>
				<!-- Restart -->
				<JFlex
					class="s-h-12 s-w-12 s-rounded-full s-bg-purple-800 s-shadow-lg s-text-white s-items-center s-justify-center s-cursor-pointer s-m-2"
					@click="handleRestart()"
				>
					<svg class="s-text-white s-h-6 s-w-6" fill="currentColor" viewBox="0 0 20 20">
						<path
							fill-rule="evenodd"
							d="M7.707 3.293a1 1 0 010 1.414L5.414 7H11a7 7 0 017 7v2a1 1 0 11-2 0v-2a5 5 0 00-5-5H5.414l2.293 2.293a1 1 0 11-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
							clip-rule="evenodd"
						></path>
					</svg>
				</JFlex>
				<!-- Exit -->
				<JFlex
					class="s-h-12 s-w-12 s-rounded-full s-bg-purple-800 s-shadow-lg s-text-white s-items-center s-justify-center s-cursor-pointer s-m-2"
					@click="handleExit()"
				>
					<svg class="s-text-white s-h-6 s-w-6" fill="currentColor" viewBox="0 0 20 20">
						<path
							fill-rule="evenodd"
							d="M3 3a1 1 0 011 1v12a1 1 0 11-2 0V4a1 1 0 011-1zm7.707 3.293a1 1 0 010 1.414L9.414 9H17a1 1 0 110 2H9.414l1.293 1.293a1 1 0 01-1.414 1.414l-3-3a1 1 0 010-1.414l3-3a1 1 0 011.414 0z"
							clip-rule="evenodd"
						></path>
					</svg>
				</JFlex>
			</JBox>
		</JBox>

		<!-- Right: CSS Inspector -->
		<JFlex class="s-flex-col s-w-4/12 s-h-full s-border-l-2 s-border-gray-300 " v-if="!isExpanded">
			<JText class="s-text-center s-font-bold s-pt-4 s-pb-2 s-border-b-2 s-border-indigo-500 s-bg-white s-select-none">
				{{ $t("uiEditor.cssInspector") }}
			</JText>
			<InspectorTailwindCss class="s-h-full s-px-4 s-py-6 s-overflow-y-scroll" v-model="nodeValue" />
		</JFlex>
	</JFlex>
</template>
<script>
import BaseComponentEditorTree from "../components/BaseComponentEditorTree"
import InspectorTailwindCss from "../components/InspectorTailwindCss"
import { generateId } from "@/utils/"

export default {
	components: { BaseComponentEditorTree, InspectorTailwindCss },

	methods: {
		/**
		 * <----- Handle methods ----->
		 */
		handleSelectNode(event) {
			this.nodeValue = event
		},

		handleSave() {
			this.themeJson = this.treeToThemeJson(this.$refs.BaseComponentEditorTree.tree)
			// TODO: Method to upsert themeJson Here
		},

		handleRestart() {
			this.tree = this.themeJsonToTree(this.themeJson)
		},

		handleExit() {
			//TODO: Methods to be called before exit & routing Here
		},

		/**
		 * <----- Compiling methods: ----->
		 */
		treeToThemeJson(array) {
			const output = new Object()
			array.forEach(obj => {
				const key = obj.key
				const value = "children" in obj ? this.treeToThemeJson(obj.children) : obj.value
				output[key] = value
			})
			return output
		},

		themeJsonToTree(object, layer) {
			const output = new Array()
			const keys = Object.keys(object)
			const currentLayer = layer ? layer + 1 : 1

			keys.forEach(key => {
				let value = object[key]

				const arrayItem = {
					key: key,
					layer: currentLayer,
					id: generateId(),
					isExpanded: currentLayer === 1 ? true : false,
					isLast: false
				}

				if (typeof value === "object") {
					value = this.themeJsonToTree(value, currentLayer)
					arrayItem.children = value
				} else {
					arrayItem.value = value
					arrayItem.isLast = true
				}
				output.push(arrayItem)
			})
			return output
		},
		/**
		 * <----- Helper Functions ----->
		 */
		// Template for adding new variants
		getJsonSkeleton(object, layer) {
			const vm = this
			const keys = Object.keys(object)
			const currentLayer = layer ? layer + 1 : 1
			const output = {}

			function recursion(key) {
				const value = object[key]
				if (typeof value === "object") {
					currentLayer === 2
						? (output["New Variant"] = vm.getJsonSkeleton(value, currentLayer))
						: (output[key] = vm.getJsonSkeleton(value, currentLayer))
				} else {
					output[key] = ""
				}
			}

			currentLayer === 2
				? recursion(keys[0])
				: keys.forEach(key => {
						recursion(key)
				  })

			return output
		},

		getTreeSkeleton(componentKey) {
			return this.themeJsonToTree(this.getJsonSkeleton(this.themeJson)).find(component => {
				return component.key === componentKey
			}).children[0]
		}
	},

	mounted() {
		// <-- TODO: Replace with real data here -->
		this.themeJson = this.dummyData
		this.tree = this.themeJsonToTree(this.themeJson)
	},

	data() {
		return {
			themeJson: {},
			tree: [],
			nodeValue: null,
			isExpanded: false,
			dummyData: {
				JButton: {
					primary: {
						el: {
							root:
								"appearance-none flex items-center justify-center px-6 py-2 border border-c0 text-base leading-normal font-medium rounded-lg bg-c10 shadow hover:bg-c9 focus:outline-none focus:shadow-focus transition-all transition-xshort transition-ease-in-out md:text-lg font-bold"
						}
					},
					"primary-small": {
						el: {
							root:
								"appearance-none flex items-center justify-center px-3 py-2 border border-c0 text-sm leading-normal font-medium rounded text-cBlack font-bold bg-c10 hover:bg-c9 focus:outline-none focus:shadow-focus transition-all transition-xshort transition-ease-in-out"
						}
					},
					secondary: {
						el: {
							root:
								"appearance-none flex items-center justify-center px-6 py-2 border border-c0 text-base leading-normal font-medium rounded-lg bg-cWhite focus:outline-none shadow focus:shadow-focus transition-all transition-xshort transition-ease-in-out md:py-4 md:text-lg font-bold"
						}
					},
					"secondary-small": {
						el: {
							root:
								"appearance-none flex items-center justify-center px-3 py-2 border border-c10 text-sm leading-normal font-medium rounded text-c10 bg-cWhite focus:outline-none shadow focus:shadow-focus active:bg-cWhite active:text-c10 transition-all transition-xshort transition-ease-in-out"
						}
					},
					"remove-small": {
						el: {
							root:
								"appearance-none flex items-center justify-center px-3 py-2 border border-c0 text-sm leading-normal font-medium rounded text-cWhite font-bold bg-cError focus:outline-none focus:shadow-focus transition-all transition-xshort transition-ease-in-out"
						}
					},
					disabled: {
						el: {
							root:
								"appearance-none flex items-center justify-center px-6 py-2 border border-c0 text-base leading-normal font-medium rounded-lg bg-c15 shadow pointer-events-none md:text-lg font-bold"
						}
					},
					"disabled-small": {
						el: {
							root:
								"appearance-none flex items-center justify-center px-3 py-2 border border-c0 text-sm leading-normal font-medium rounded text-cWhite bg-c15 shadow pointer-events-none"
						}
					},
					"mobile-menu-login": {
						el: {
							root:
								"appearance-none block w-full px-5 py-3 text-center leading-normal font-medium text-c4 bg-c15 hover:bg-c2 hover:text-c4 focus:outline-none focus:bg-c2 focus:text-c4 transition transition-xshort transition-ease-in-out"
						}
					},
					hero: {
						el: {
							root:
								"w-full flex items-center justify-center px-8 py-3 border border-transparent text-base leading-6 font-medium rounded-md text-cWhite bg-c4 hover:bg-c3 focus:outline-none focus:shadow-outline transition duration-150 ease-in-out md:py-4 md:text-lg md:px-10"
						}
					}
				},
				JInputText: {
					default: {
						description: "You can describe what it is",
						el: {
							root:
								"px-3 py-2 appearance-none relative block w-full bg-cWhite border border-c15 rounded shadow focus:z-10 focus:outline-none focus:border-c4 focus:shadow-focus transition transition-ease-in-out transition-xshort sm:text-sm sm:leading-normal text-sm"
						}
					},
					disabled: {
						description: "You can describe what it is",
						el: {
							root:
								"px-3 py-2 appearance-none relative block w-full border border-c15 rounded shadow bg-c15 focus:outline-none sm:text-sm sm:leading-normal pointer-events-none"
						}
					}
				},
				JSelect: {
					default: {
						el: {
							root: "inline-block relative w-full",
							select:
								"block appearance-none w-full px-3 py-2 relative sm:text-sm sm:leading-normal border border-c15 rounded  shadow bg-cWhite focus:z-10 focus:outline-none focus:border-c4 focus:shadow-focus transition transition-ease-in-out transition-xshort",
							iconRoot: "pointer-events-none absolute inset-y-0 right-0 mr-2 flex items-center",
							icon: "fill-current h-3 w-3"
						}
					},
					hero: {
						el: {
							root: "inline-block relative",
							select:
								"block appearance-none w-full px-2 pr-6 py-1 relative text-3xl font-bold leading-none border border-c0 rounded bg-c4 focus:z-10 focus:outline-none focus:shadow-focus transition transition-ease-in-out transition-xshort",
							iconRoot: "pointer-events-none absolute inset-y-0 right-0 mr-2 flex items-center",
							icon: "fill-current h-3 w-3"
						}
					}
				},
				JRadios: {
					default: {
						el: {
							root: "text-sm flex flex-auto select-none",
							item:
								"flex items-center px-3 bg-c15 text-cBlack rounded border border-c0 cursor-pointer mr-2 last:mr-0 h-10 focus:outline-none text-sm",
							itemSelected:
								"flex items-center px-3 bg-c10 text-cBlack rounded border border-c0 cursor-pointer mr-2 h-10 focus:outline-none text-sm"
						}
					}
				},
				JModalSimple: {
					default: {
						el: {
							root: "m-auto shadow-2 max-h-modal w-full overflow-y-auto max-w-xl relative",
							overlay: "fixed flex items-center top-0 left-0 w-full h-full bg-cBlack-a50 p-4 z-50",
							enter: "opacity-0",
							enterActive: "transition transition-medium transition-ease",
							leaveActive: "transition transition-medium transition-ease-out",
							leaveTo: "opacity-0"
						}
					},
					small: {
						el: {
							root: "m-auto shadow-2 max-h-modal w-full overflow-y-auto max-w-md relative",
							overlay: "fixed flex items-center top-0 left-0 w-full h-full bg-cBlack-a50 p-4 z-50",
							enter: "opacity-0",
							enterActive: "transition transition-medium transition-ease",
							leaveActive: "transition transition-medium transition-ease-out",
							leaveTo: "opacity-0"
						}
					},
					"3xl": {
						el: {
							root: "m-auto shadow-2 max-h-modal w-full overflow-y-auto max-w-3xl bg-c5",
							overlay: "fixed flex items-center top-0 left-0 w-full h-full bg-cBlack-a50 p-4",
							enter: "opacity-0",
							enterActive: "transition transition-medium transition-ease",
							leaveActive: "transition transition-medium transition-ease-out",
							leaveTo: "opacity-0"
						}
					}
				},
				JToggle: {
					default: {
						el: {
							sliderTrue: "w-8 h-4 rounded-full shadow inline-block cursor-pointer bg-c15 focus:outline-none focus:shadow-focus",
							sliderFalse: "w-8 h-4 rounded-full shadow inline-block cursor-pointer bg-c15 focus:outline-none focus:shadow-focus",
							switchTrue: "w-4 h-4 bg-cWhite rounded-full shadow",
							switchFalse: "w-4 h-4 bg-cWhite rounded-full shadow"
						}
					},
					primary: {
						el: {
							sliderTrue: "w-12 h-6 rounded-full shadow inline-block cursor-pointer bg-cSuccess focus:outline-none",
							sliderFalse: "w-12 h-6 rounded-full shadow inline-block cursor-pointer bg-c15 focus:outline-none",
							switchTrue: "w-6 h-6 bg-cWhite rounded-full shadow",
							switchFalse: "w-6 h-6 bg-cWhite rounded-full shadow"
						}
					},
					"primary-small": {
						el: {
							sliderTrue: "w-8 h-4 rounded-full shadow inline-block cursor-pointer bg-cSuccess focus:outline-none",
							sliderFalse: "w-8 h-4 rounded-full shadow inline-block cursor-pointer bg-c15 focus:outline-none",
							switchTrue: "w-4 h-4 bg-cWhite rounded-full shadow",
							switchFalse: "w-4 h-4 bg-cWhite rounded-full shadow"
						}
					}
				},
				JDatePicker: {
					default: {
						el: {
							root: "w-full",
							input:
								"px-3 py-2 appearance-none relative block w-full h-10 bg-cWhite border border-c1 rounded shadow focus:z-10 focus:outline-none focus:border-c4 focus:shadow-focus transition transition-ease-in-out transition-xshort text-sm leading-normal cursor-pointer"
						},
						componentInstanceVariants: {
							calendar: "default"
						}
					},
					hidden: {
						el: {
							root: "w-full",
							input:
								"px-3 py-2 appearance-none relative block w-full h-10 bg-cWhite border border-c1 rounded shadow-3 focus:z-10 focus:outline-none focus:border-c4 focus:shadow-focus transition transition-ease-in-out transition-xshort sm:text-sm sm:leading-normal opacity-0 cursor-pointer"
						},
						componentInstanceVariants: {
							calendar: "default"
						}
					}
				},
				JCalendar: {
					default: {
						el: {
							calendar: "w-64 px-2 border-c4 border rounded absolute top-0 z-10 bg-cWhite mt-8",
							navigation: "py-4 flex justify-center items-center",
							changeMonthButtons: "flex-initial mx-1 text-xs p-2 cursor-pointer",
							weekdaysWrapper: "flex justify-between py-2",
							weekday: "flex-1 text-xs text-c1 font-bold justify-between text-center",
							monthYearSelect: "flex-1 mx-1 bg-c8 p-1 rounded leading-normal text-sm font-bold",
							datesWrapper: "flex flex-wrap justify-between",
							date: "py-2 text-c1 hover:bg-c14",
							dateOfActiveMonth: "font-semibold py-2 text-cBlack hover:bg-c15",
							selectedDate: "py-2 bg-c10 text-c4 font-bold rounded-full",
							disabledDate: "cursor-not-allowed py-2 text-c3"
						}
					},
					inline: {
						el: {
							calendar: "w-64 px-2 border-c4 border rounded",
							navigation: "py-4 flex justify-center items-center",
							changeMonthButtons: "flex-initial mx-1 text-xs p-2 cursor-pointer",
							weekdaysWrapper: "flex justify-between border-b py-2",
							weekday: "flex-1 text-xs text-gray-700 font-bold justify-between text-center",
							monthYearSelect: "flex-1 mx-1 bg-c4 p-1 rounded leading-normal text-sm font-bold",
							datesWrapper: "flex flex-wrap justify-between",
							date: "py-2 border-b border-c4 text-c6 hover:bg-c9",
							dateOfActiveMonth: "font-semibold py-2 border-b border-c4 text-c7 hover:bg-c9",
							selectedDate: "py-2 bg-c2 text-cWhite font-bold",
							disabledDate: "cursor-not-allowed py-2 text-c4"
						}
					}
				},
				JInputDateOfBirth: {
					default: {
						el: {
							root: "w-full flex flex-wrap bg-c0",
							day: "w-full flex-grow md:w-auto mb-2 md:mb-0 md:mr-2 focus:outline-none focus:shadow-focus",
							month: "w-full flex-grow md:w-auto mb-2 md:mb-0 md:mr-2 focus:outline-none focus:shadow-focus",
							year: "w-full flex-grow md:w-auto mb-2 md:mb-0 focus:outline-none focus:shadow-focus"
						},
						componentInstanceVariants: {
							daySelect: "default",
							monthSelect: "default",
							yearSelect: "default"
						}
					}
				},
				JSpinner: {
					default: {
						el: {
							circle: "inline-block w-8 h-10 border-t-2 rounded-full border-c4",
							dots: "flex justify-center items-center w-auto h-auto",
							dotsItem: "w-4 h-4 mx-1 rounded-full inline-block bg-c4"
						}
					},
					small: {
						el: {
							circle: "inline-block w-4 h-4 border-t-2 rounded-full border-c4",
							dots: "flex justify-center items-center w-auto h-auto",
							dotsItem: "w-2 h-2 mx-1 rounded-full inline-block bg-c4"
						}
					},
					"small-white": {
						el: {
							circle: "inline-block w-4 h-4 border-t-2 rounded-full border-cWhite",
							dots: "flex justify-center items-center w-auto h-auto",
							dotsItem: "w-4 h-4 mx-1 rounded-full inline-block bg-c4"
						}
					}
				},
				JMessage: {
					default: {
						el: {
							root: "w-full fixed bottom-0 left-0 p-4",
							messageSuccess:
								"relative w-full md:w-4/12 p-4 pr-8 rounded shadow-2 inline-block cursor-pointer bg-cSuccess mb-2 focus:outline-none focus:shadow-focus text-cWhite leading-normal",
							messageError:
								"relative w-full md:w-4/12 p-4 pr-8 rounded shadow-2 inline-block cursor-pointer bg-cError mb-2 focus:outline-none focus:shadow-focus text-cWhite leading-normal",
							messageWarning:
								"relative w-full md:w-4/12 p-4 pr-8 text-cBlack rounded shadow-2 inline-block cursor-pointer bg-cWarning mb-2 focus:outline-none focus:shadow-focus",
							messageNeutral:
								"relative w-full md:w-4/12 p-4 pr-8 text-cBlack rounded shadow-2 inline-block cursor-pointer bg-cWhite mb-2 focus:outline-none focus:shadow-focus",
							closeIcon:
								"absolute top-0 right-0 mr-2 mt-2 w-5 h-5 rounded-full flex justify-center items-center text-cWhite hover:bg-cBlack hover:opacity-50",
							enter: "opacity-0",
							enterActive: "transition transition-medium transition-ease",
							leaveActive: "transition transition-medium transition-ease-out",
							leaveTo: "opacity-0"
						}
					}
				},
				JCheckbox: {
					default: {
						el: {
							root: "inline-block cursor-pointer",
							hiddenInput: "w-0 h-0 opacity-0 absolute",
							checkbox: "flex items-center w-4 h-4 rounded border border-c1 cursor-pointer focus:outline-none",
							checkboxChecked:
								"text-cWhite flex justify-center items-center w-4 h-4 rounded bg-cWhite border border-cSuccess cursor-pointer focus:outline-none text-cSuccess font-bold"
						},
						assets: {
							iconChecked: "&#x2713;",
							iconIndeterminate: "&#8259;"
						}
					},
					disabled: {
						el: {
							root: "inline-block cursor-pointer",
							hiddenInput: "w-0 h-0 opacity-0 absolute",
							checkbox:
								"s-pointer-events-none s-cursor-not-allowed pointer-events-none  flex items-center w-6 h-6 rounded border bg-c4 border-c4 cursor-pointer focus:outline-none focus:border-c3",
							checkboxChecked:
								"s-pointer-events-none s-cursor-not-allowed pointer-events-none  text-c4 flex items-center w-6 h-6 rounded bg-c4 border border-c4 cursor-pointer focus:outline-none focus:border-c3"
						},
						assets: {
							iconChecked: "&#x2713;",
							iconIndeterminate: "&#8259;"
						}
					},
					rounded: {
						el: {
							root: "inline-block cursor-pointer",
							hiddenInput: "w-0 h-0 opacity-0 absolute",
							checkbox:
								"flex items-center w-6 h-6 rounded-full border border-c2 cursor-pointer focus:outline-none focus:border-c3",
							checkboxChecked:
								"text-pink-500 flex items-center w-6 h-6 rounded-full bg-pink-100 border border-c2 cursor-pointer focus:outline-none focus:border-c3"
						},
						assets: {
							iconChecked: "&#x2713;",
							iconIndeterminate: "&#8259;"
						}
					}
				},
				JMultiSelect: {
					default: {
						el: {
							root: "",
							tag: "flex w-full items-center justify-between mt-2 border border-c15 bg-c14 rounded px-3 py-2",
							input:
								"px-3 py-2 appearance-none relative block w-full bg-cWhite border border-c15 rounded shadow focus:z-10 focus:outline-none focus:border-c4 focus:shadow-focus transition transition-ease-in-out transition-xshort sm:text-sm sm:leading-normal text-sm",
							options: "bg-cWhite border-c15 border border-t-0 shadow",
							option: "text-cBlack hover:bg-c3 p-2",
							transition: {
								enter: "opacity-0",
								leave: "opacity-0",
								enterActive: "transition transition-medium transition-ease",
								leaveActive: "transition transition-short transition-ease-out"
							}
						}
					},
					"tags-hidden": {
						el: {
							root: "",
							tag: "hidden",
							input:
								"px-3 py-2 appearance-none relative block w-full bg-cWhite border border-c15 rounded shadow focus:z-10 focus:outline-none focus:border-c4 focus:shadow-focus transition transition-ease-in-out transition-xshort sm:text-sm sm:leading-normal text-sm",
							options: "bg-cWhite border-c15 border border-t-0 shadow",
							option: "text-cBlack hover:bg-c3 p-2",
							transition: {
								enter: "opacity-0",
								leave: "opacity-0",
								enterActive: "transition transition-medium transition-ease",
								leaveActive: "transition transition-short transition-ease-out"
							}
						}
					},
					dark: {
						el: {
							root: "rounded-sm bg-c1 border-c8 border",
							tag: "p-2 rounded bg-c3 m-1 text-cWhite text-sm",
							input: "focus:outline-none p-2 w-full bg-c1",
							options: "bg-c1 border-c8 border border-t-0",
							option: "text-cWhite hover:bg-cBlack p-2",
							transition: {
								enter: "opacity-0",
								leave: "opacity-0",
								enterActive: "transition transition-medium transition-ease",
								leaveActive: "transition transition-short transition-ease-out"
							}
						}
					}
				}
			}
		}
	}
}
</script>
